import { z } from "zod";
import { type Locale } from "./locale.js";

// Localized free-text field for content WE author, stored in DDB
// (template name / description / notes / session label / slot
// instructions). English is required as the deterministic fallback —
// every entry we ship has at least an EN copy, so the renderer can
// always return a non-empty string without per-call null handling.
//
// Distinct from `LocalizedDescription` (schemas/exercise.ts), where
// every locale is optional because the source data is user-typed and
// may have nothing in any specific language. Here we control the
// source and can enforce EN.
export const LocalizedText = z.object({
  en: z.string().min(1),
  it: z.string().min(1).optional(),
  fr: z.string().min(1).optional(),
  de: z.string().min(1).optional(),
  ru: z.string().min(1).optional(),
});
export type LocalizedText = z.infer<typeof LocalizedText>;

// Resolve to the preferred locale, fall back to EN. Trims so a value
// of " " (spaces only) is treated as missing — the schema's min(1)
// already blocks empty strings at parse time, but defence-in-depth
// keeps a buggy author entry from rendering whitespace.
export const pickLocalizedText = (
  text: LocalizedText,
  preferred: Locale,
): string => {
  const v = text[preferred];
  if (v && v.trim().length > 0) return v;
  return text.en;
};

// Concatenate every non-empty locale string into a single haystack.
// Used by catalog search so a user typing in any of the supported
// languages can match an exercise even when its localised name is in
// a different language they happen to also know. Returns a single
// space-separated string ready for case-fold / normalisation.
export const allLocalizedStrings = (text: LocalizedText): string =>
  [text.en, text.it, text.fr, text.de, text.ru]
    .filter((s): s is string => typeof s === "string" && s.trim().length > 0)
    .join(" ");
